"use client"

import React, { useState, useRef, useEffect, useCallback } from "react"
import { cx } from "class-variance-authority"
import { AnimatePresence, motion } from "motion/react"
import { useLocation, useNavigate } from "react-router-dom"
import { 
  Send, X, Trash2, Sparkles, Wrench, Lightbulb, Check, CheckCircle2, 
  MessageSquare, LifeBuoy, ExternalLink, Store, MapPin, Info, Bot 
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { useRigAssistant, stripSaveRigCommand, createSupportTicket, CreateTicketData } from "@/hooks/useRigAssistant"
import { useRigContextSafe } from "@/contexts/RigContext"
import { useAuth } from "@/contexts/AuthContext"
import { useGlobalSettings } from "@/hooks/useGlobalSettings"
import { ChatSaveRigCard } from "@/components/ChatSaveRigCard"
import { useIsMobile } from "@/hooks/use-mobile"

// ============================================
// ColorOrb Component
// ============================================
interface OrbProps {
  dimension?: string
  className?: string
  tones?: {
    base?: string
    accent1?: string
    accent2?: string
    accent3?: string
  }
  spinDuration?: number
}

const ColorOrb: React.FC<OrbProps> = ({
  dimension = "192px",
  className,
  tones,
  spinDuration = 20,
}) => {
  const fallbackTones = {
    base: "oklch(95% 0.02 264.695)",
    accent1: "oklch(75% 0.15 350)",
    accent2: "oklch(80% 0.12 200)",
    accent3: "oklch(78% 0.14 280)",
  }

  const palette = { ...fallbackTones, ...tones }
  const dimValue = parseInt(dimension.replace("px", ""), 10)

  const blurStrength = dimValue < 50 ? Math.max(dimValue * 0.008, 1) : Math.max(dimValue * 0.015, 4)
  const contrastStrength = dimValue < 50 ? Math.max(dimValue * 0.004, 1.2) : Math.max(dimValue * 0.008, 1.5)
  const pixelDot = dimValue < 50 ? Math.max(dimValue * 0.004, 0.05) : Math.max(dimValue * 0.008, 0.1)
  const shadowRange = dimValue < 50 ? Math.max(dimValue * 0.004, 0.5) : Math.max(dimValue * 0.008, 2)
  const maskRadius = dimValue < 30 ? "0%" : dimValue < 50 ? "5%" : dimValue < 100 ? "15%" : "25%"
  const adjustedContrast = dimValue < 30 ? 1.1 : dimValue < 50 ? Math.max(contrastStrength * 1.2, 1.3) : contrastStrength

  return (
    <div
      className={cn("color-orb", className)}
      style={{
        "--base": palette.base,
        "--accent1": palette.accent1,
        "--accent2": palette.accent2,
        "--accent3": palette.accent3,
        "--blur": `${blurStrength}px`,
        "--contrast": adjustedContrast,
        "--dot": `${pixelDot}px`,
        "--shadow": `${shadowRange}px`,
        "--mask": maskRadius,
        "--spin-duration": `${spinDuration}s`,
        width: dimension,
        height: dimension,
      } as React.CSSProperties}
    >
      <style>{`
        @property --angle {
          syntax: "<angle>";
          inherits: false;
          initial-value: 0deg;
        }
        .color-orb {
          display: grid;
          grid-template-areas: "stack";
          overflow: hidden;
          border-radius: 50%;
          position: relative;
          transform: scale(1.1);
        }
        .color-orb::before,
        .color-orb::after {
          content: "";
          display: block;
          grid-area: stack;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          transform: translateZ(0);
        }
        .color-orb::before {
          background:
            conic-gradient(from calc(var(--angle) * 2) at 25% 70%, var(--accent3), transparent 20% 80%, var(--accent3)),
            conic-gradient(from calc(var(--angle) * 2) at 45% 75%, var(--accent2), transparent 30% 60%, var(--accent2)),
            conic-gradient(from calc(var(--angle) * -3) at 80% 20%, var(--accent1), transparent 40% 60%, var(--accent1)),
            conic-gradient(from calc(var(--angle) * 2) at 15% 5%, var(--accent2), transparent 10% 90%, var(--accent2)),
            conic-gradient(from calc(var(--angle) * 1) at 20% 80%, var(--accent1), transparent 10% 90%, var(--accent1)),
            conic-gradient(from calc(var(--angle) * -2) at 85% 10%, var(--accent3), transparent 20% 80%, var(--accent3));
          box-shadow: inset var(--base) 0 0 var(--shadow) calc(var(--shadow) * 0.2);
          filter: blur(var(--blur)) contrast(var(--contrast));
          animation: spin var(--spin-duration) linear infinite;
        }
        .color-orb::after {
          background-image: radial-gradient(circle at center, var(--base) var(--dot), transparent var(--dot));
          background-size: calc(var(--dot) * 2) calc(var(--dot) * 2);
          backdrop-filter: blur(calc(var(--blur) * 2)) contrast(calc(var(--contrast) * 2));
          mix-blend-mode: overlay;
        }
        .color-orb[style*="--mask: 0%"]::after { mask-image: none; }
        .color-orb:not([style*="--mask: 0%"])::after { mask-image: radial-gradient(black var(--mask), transparent 75%); }
        @keyframes spin { to { --angle: 360deg; } }
        @media (prefers-reduced-motion: reduce) { .color-orb::before { animation: none; } }
      `}</style>
    </div>
  )
}

// Animation constants
const SPEED_FACTOR = 1
const SPRING_MORPH = { type: "spring", stiffness: 400 / SPEED_FACTOR, damping: 35 } as const
const SPRING_LOGO = { type: "spring", stiffness: 350 / SPEED_FACTOR, damping: 35 } as const

// Dock dimensions
const DOCK_WIDTH = 160
const DOCK_HEIGHT = 48
const FORM_WIDTH = 520
const FORM_HEIGHT = 600

// ============================================
// Main Export: RigAssistantChat
// ============================================
export function RigAssistantChat() {
  const location = useLocation()
  if (location.pathname.startsWith('/admin')) return null
  return <RigAssistantChatInner />
}

function RigAssistantChatInner() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { settings, isLoading: settingsLoading } = useGlobalSettings()
  const rigContext = useRigContextSafe()
  const context = rigContext?.context
  const { user, roles } = useAuth()
  
  // Responsive dimensions
  const dockWidth = isMobile ? 120 : 140
  const dockHeight = isMobile ? 40 : 44
  const formWidth = isMobile ? 'calc(100vw - 2rem)' : 400
  const formHeight = isMobile ? 'calc(100dvh - 6rem)' : 520
  
  // Debug: log context changes
  useEffect(() => {
    console.log('[AI-Input] Context updated:', context)
  }, [context])

  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const [charCount, setCharCount] = useState(0)
  const maxChars = 250

  const {
    messages,
    isLoading,
    sendMessage,
    clearMessages,
    maxAiMessages,
    currentAiMessageCount,
    canSendAiMessage,
    pendingSaveRig,
    clearPendingSaveRig
  } = useRigAssistant()

  const isLimitReached = !canSendAiMessage()
  const [dismissedSaveRig, setDismissedSaveRig] = useState(false)
  const [pendingTicketDescription, setPendingTicketDescription] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const getPlanInfo = () => {
    if (!user) return { label: 'Guest', color: 'bg-muted text-muted-foreground' }
    if (roles.includes('admin')) return { label: 'Admin', color: 'bg-red-600 text-white' }
    if (roles.includes('business')) return { label: 'Business', color: 'bg-gradient-to-r from-amber-500 to-orange-600 text-white' }
    if (roles.includes('pro')) return { label: 'Pro', color: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' }
    return { label: 'Free', color: 'bg-muted text-muted-foreground' }
  }
  const planInfo = getPlanInfo()

  const isChatbotDisabled = !settingsLoading && !settings.chatbot_settings.enabled

  const orbTones = isChatbotDisabled ? {
    base: "oklch(30% 0 0)",
    accent1: "oklch(40% 0 0)",
    accent2: "oklch(35% 0 0)",
    accent3: "oklch(38% 0 0)",
  } : {
    base: "oklch(50% 0.2 270)",
    accent1: "oklch(70% 0.25 300)",
    accent2: "oklch(65% 0.2 250)",
    accent3: "oklch(60% 0.22 280)",
  }

  // Auto-scroll
  useEffect(() => {
    if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
  }, [isOpen])

  useEffect(() => {
    if (isOpen && messages.length > 0) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isOpen])

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100)
    }
  }, [isOpen])

  // External open requests
  useEffect(() => {
    const handleOpenChatbot = (event: Event) => {
      const customEvent = event as CustomEvent<{ initialMessage?: string }>
      setIsOpen(true)
      if (customEvent.detail?.initialMessage) {
        setTimeout(() => sendMessage(customEvent.detail.initialMessage!, context), 150)
      }
    }
    window.addEventListener('open-rig-assistant', handleOpenChatbot)
    return () => window.removeEventListener('open-rig-assistant', handleOpenChatbot)
  }, [context, sendMessage])

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node) && isOpen) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    if (value.length <= maxChars) {
      setInput(value)
      setCharCount(value.length)
    }
  }

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (input.trim() && !isLoading) {
      console.log('[AI-Input] Sending message with context:', context)
      sendMessage(input, context)
      setInput('')
      setCharCount(0)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
    if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const quickPrompts = [
    "What can you help me with?",
    "What's the best battery setup for all-day shooting?",
    "Help me build a lightweight run & gun rig",
    "What accessories do I need for gimbal work?",
    "I'd like to suggest a rental house",
  ]

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50" ref={wrapperRef}>
      <motion.div
        className="relative"
        initial={false}
        animate={{
          width: isOpen ? formWidth : dockWidth,
          height: isOpen ? formHeight : dockHeight,
          borderRadius: isMobile ? 20 : 24,
        }}
        transition={SPRING_MORPH}
        style={{
          background: 'linear-gradient(145deg, hsl(var(--card)), hsl(var(--background) / 0.98))',
          boxShadow: isOpen 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px hsl(var(--border) / 0.5)'
            : '0 10px 30px -8px rgba(0, 0, 0, 0.3), 0 0 0 1px hsl(var(--border) / 0.3)',
          maxWidth: isMobile ? 'calc(100vw - 2rem)' : 'calc(100vw - 3rem)',
          maxHeight: isMobile ? 'calc(100dvh - 2rem)' : undefined,
          overflow: 'hidden',
        }}
      >
        {/* Dock Bar State (minimized) */}
        <motion.button
          initial={false}
          animate={{ 
            opacity: isOpen ? 0 : 1,
          }}
          transition={{ duration: 0.15 }}
          onClick={() => setIsOpen(true)}
          className="absolute inset-0 flex items-center justify-center gap-1.5 sm:gap-2 cursor-pointer hover:bg-secondary/20 transition-colors"
          style={{ pointerEvents: isOpen ? 'none' : 'auto' }}
        >
          <div className="relative">
            <ColorOrb dimension={isMobile ? "28px" : "32px"} tones={orbTones} spinDuration={6} />
            <div className={cn(
              "absolute -top-0.5 -right-0.5 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full border-2 border-background",
              isChatbotDisabled ? "bg-red-500" : "bg-green-500"
            )} />
          </div>
          <span className="text-xs sm:text-sm font-medium text-foreground">Ask AI</span>
        </motion.button>

        {/* Expanded Chat State */}
        <motion.div
          initial={false}
          animate={{ 
            opacity: isOpen ? 1 : 0,
          }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 flex flex-col"
          style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
        >
              {/* Header - clickable to collapse */}
              <div 
                onClick={() => setIsOpen(false)}
                className="px-3 py-2 sm:px-4 sm:py-3 border-b border-border/50 flex-shrink-0 cursor-pointer hover:bg-secondary/30 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="relative">
                      <ColorOrb dimension={isMobile ? "24px" : "28px"} tones={orbTones} spinDuration={6} />
                    </div>
                    <div className="text-left">
                      <span className="text-foreground font-semibold text-xs sm:text-sm block">AI Rig Assistant</span>
                      {(isChatbotDisabled || context?.cameraName) && (
                        <span className="text-[10px] text-muted-foreground truncate max-w-[120px] sm:max-w-none block">
                          {isChatbotDisabled ? 'Currently unavailable' : `Building for ${context.cameraName}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2" onClick={(e) => e.stopPropagation()}>
                    {!isChatbotDisabled && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-[10px] sm:text-xs text-muted-foreground">
                          {maxAiMessages === -1 ? '∞' : `${currentAiMessageCount}/${maxAiMessages}`}
                        </span>
                        <span className={cn("px-1.5 sm:px-2 py-0.5 text-[10px] font-medium rounded-md", planInfo.color)}>
                          {planInfo.label}
                        </span>
                      </div>
                    )}
                    {isChatbotDisabled && (
                      <span className="px-1.5 sm:px-2 py-0.5 text-[10px] font-medium rounded-md bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        Maintenance
                      </span>
                    )}
                    {!isChatbotDisabled && messages.length > 0 && (
                      <button onClick={clearMessages} className="p-1 sm:p-1.5 rounded-full hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground" title="Clear chat">
                        <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      </button>
                    )}
                    <button onClick={() => setIsOpen(false)} className="p-1 sm:p-1.5 rounded-full hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground">
                      <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Maintenance Mode */}
              {isChatbotDisabled ? (
                <div className="flex-1 flex flex-col items-center justify-center py-12 px-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                    <Wrench className="w-8 h-8 text-amber-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Under Maintenance</h3>
                  <p className="text-sm text-muted-foreground max-w-xs mb-4">We're making improvements. It will be back online shortly!</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                    <span>Offline</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <ScrollArea className="flex-1 px-4 py-3">
                    {messages.length === 0 ? (
                      <div className="space-y-3">
                        <div className="text-center py-4">
                          <h4 className="font-medium mb-1 text-foreground text-sm">How can I help?</h4>
                          <p className="text-xs text-muted-foreground">Describe your shoot and I'll recommend the perfect rig setup.</p>
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Quick prompts</p>
                          {quickPrompts.map((prompt, i) => (
                            <button
                              key={i}
                              onClick={() => { setInput(prompt); setCharCount(prompt.length); textareaRef.current?.focus() }}
                              className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-secondary/50 hover:bg-secondary text-foreground transition-colors border border-border/30 hover:border-primary/30"
                            >
                              {prompt}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((message, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={cn("flex", message.role === 'user' ? 'justify-end' : 'justify-start')}
                          >
                            <div className={cn(
                              "max-w-[85%] rounded-xl px-3 py-2 text-xs",
                              message.role === 'user'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-secondary/80 text-foreground border border-border/50'
                            )}>
                              {message.role === 'assistant' ? (
                                <div className="prose prose-sm prose-invert max-w-none">
                                  <MessageContent
                                    content={stripSaveRigCommand(message.content)}
                                    onRecommendationConfirm={() => sendMessage("Yes, please submit the recommendation", context)}
                                    onWhySubmit={(reason) => sendMessage(reason, context)}
                                    onRentalSuggestionConfirm={() => sendMessage("Yes, please submit the rental house suggestion", context)}
                                    onRentalSuggestionDetailsSubmit={(details) => sendMessage(details, context)}
                                    onTicketCreate={async (data) => { 
                                      await createSupportTicket({
                                        subject: data.subject,
                                        description: pendingTicketDescription || data.description
                                      }); 
                                      setPendingTicketDescription(null);
                                      sendMessage("The ticket has been created.", context) 
                                    }}
                                    onTicketGatheringSubmit={(details) => {
                                      setPendingTicketDescription(details);
                                      sendMessage(details, context);
                                    }}
                                    onViewTickets={() => navigate('/support')}
                                    isLatestMessage={i === messages.length - 1}
                                  />
                                </div>
                              ) : message.content}
                              {message.role === 'assistant' && i === messages.length - 1 && pendingSaveRig && !dismissedSaveRig && (
                                <ChatSaveRigCard saveData={pendingSaveRig} onSaved={() => { clearPendingSaveRig(); setDismissedSaveRig(false) }} onDismiss={() => setDismissedSaveRig(true)} />
                              )}
                            </div>
                          </motion.div>
                        ))}
                        {isLoading && messages[messages.length - 1]?.role === 'user' && (
                          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                            <div className="bg-secondary/80 rounded-xl px-3 py-2 border border-border/50">
                              <div className="flex items-center gap-2">
                                <ColorOrb dimension="14px" spinDuration={2} tones={orbTones} />
                                <span className="text-[10px] text-muted-foreground animate-pulse">Thinking...</span>
                              </div>
                            </div>
                          </motion.div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>

                  {/* Input Section */}
                  <div className="flex-shrink-0 border-t border-border/50">
                    {isLimitReached ? (
                      <div className="px-4 py-3 text-center">
                        <p className="text-xs text-muted-foreground mb-2">You've reached your AI message limit</p>
                        <Button onClick={() => navigate('/pricing')} size="sm" className="gap-2">
                          <Sparkles className="w-3.5 h-3.5" />
                          Upgrade for more
                        </Button>
                      </div>
                    ) : (
                      <div className="px-3 pt-3 pb-2">
                        <div className="flex items-end gap-2">
                          <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Describe your shoot..."
                            maxLength={maxChars}
                            rows={2}
                            disabled={isLoading}
                            className="flex-1 px-3 py-2 bg-secondary/50 border border-border/50 rounded-xl text-xs text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all disabled:opacity-50"
                          />
                          <Button onClick={() => handleSubmit()} disabled={!input.trim() || isLoading} size="icon" className="h-10 w-10 rounded-xl flex-shrink-0">
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30 text-[10px] text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <KeyHint>⌘</KeyHint>
                              <KeyHint>Enter</KeyHint>
                            </div>
                            <span className="text-border">•</span>
                            <span className={cn(charCount > maxChars * 0.9 && 'text-amber-500')}>{charCount}/{maxChars}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                            <span>Online</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
        </motion.div>
      </motion.div>
    </div>
  )
}

// ============================================
// Message Card Components
// ============================================

function RecommendationOfferCard({ content, onConfirm, isLatest }: { content: string; onConfirm?: () => void; isLatest: boolean }) {
  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-primary/15 via-amber-500/10 to-primary/15 border border-primary/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-amber-500/5 animate-pulse" style={{ animationDuration: '3s' }} />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-primary/20"><Lightbulb className="w-3 h-3 text-primary" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">Recommendation</span>
        </div>
        <p className="text-xs text-foreground leading-relaxed mb-2">{content}</p>
        {isLatest && onConfirm && (
          <button onClick={onConfirm} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/20 hover:bg-primary/30 border border-primary/40 rounded-md text-[10px] font-medium text-primary transition-all">
            <Check className="w-3 h-3" />Submit
          </button>
        )}
      </div>
    </div>
  )
}

function WhyQuestionCard({ metadata, onSubmit, isLatest }: { metadata: { equipmentName: string; equipmentType: string; brand?: string }; onSubmit?: (reason: string) => void; isLatest: boolean }) {
  const [reason, setReason] = useState('')
  const maxChars = 300
  const handleSubmit = () => { if (reason.trim() && onSubmit) { onSubmit(reason.trim()); setReason('') } }

  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-blue-500/15 via-indigo-500/10 to-blue-500/15 border border-blue-500/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-blue-500/20"><MessageSquare className="w-3 h-3 text-blue-400" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-400">Tell Us Why</span>
        </div>
        <p className="text-xs text-foreground leading-relaxed mb-2">Why would <strong className="text-blue-300">{metadata.equipmentName}</strong> be a good addition?</p>
        {isLatest && onSubmit && (
          <div className="space-y-2">
            <textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, maxChars))} placeholder="E.g., Great for hybrid shooting..." className="w-full px-2 py-1.5 bg-background/70 border border-blue-500/30 rounded-md text-[10px] text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-blue-500/50" rows={2} />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">{reason.length}/{maxChars}</span>
              <button onClick={handleSubmit} disabled={!reason.trim()} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 rounded-md text-[10px] font-medium text-blue-400 disabled:opacity-50">
                <Send className="w-3 h-3" />Submit
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function RecommendationSentCard({ metadata }: { metadata: { name: string; type: string } }) {
  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-green-500/15 via-emerald-500/10 to-green-500/15 border border-green-500/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-emerald-500/5" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-green-500/20"><CheckCircle2 className="w-3 h-3 text-green-400" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-green-400">Submitted</span>
        </div>
        <p className="text-xs text-foreground leading-relaxed">Recommendation for <strong className="text-green-300">{metadata.name}</strong> ({metadata.type}) submitted.</p>
      </div>
    </div>
  )
}

function TicketGatheringCard({ metadata, onSubmit, isLatest }: { metadata: { placeholder?: string; category?: string }; onSubmit?: (details: string) => void; isLatest: boolean }) {
  const [details, setDetails] = useState('')
  const maxChars = 500
  const handleSubmit = () => { if (details.trim() && onSubmit) { onSubmit(details.trim()); setDetails('') } }

  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-purple-500/15 via-violet-500/10 to-purple-500/15 border border-purple-500/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-violet-500/5" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-purple-500/20"><LifeBuoy className="w-3 h-3 text-purple-400" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-purple-400">Support Ticket</span>
        </div>
        <p className="text-xs text-foreground leading-relaxed mb-2">{metadata.placeholder || "Please describe your issue."}</p>
        {isLatest && onSubmit && (
          <div className="space-y-2">
            <textarea value={details} onChange={(e) => setDetails(e.target.value.slice(0, maxChars))} placeholder="Describe what's happening..." className="w-full px-2 py-1.5 bg-background/70 border border-purple-500/30 rounded-md text-[10px] text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-purple-500/50" rows={2} />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">{details.length}/{maxChars}</span>
              <button onClick={handleSubmit} disabled={!details.trim()} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 rounded-md text-[10px] font-medium text-purple-400 disabled:opacity-50">
                <Send className="w-3 h-3" />Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function TicketOfferCard({ metadata, onCreate, isLatest }: { metadata: { subject: string; description: string }; onCreate?: () => void; isLatest: boolean }) {
  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-purple-500/15 via-violet-500/10 to-purple-500/15 border border-purple-500/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-violet-500/5 animate-pulse" style={{ animationDuration: '3s' }} />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-purple-500/20"><LifeBuoy className="w-3 h-3 text-purple-400" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-purple-400">Ticket Ready</span>
        </div>
        <div className="space-y-1 mb-2">
          <p className="text-xs text-foreground"><strong className="text-purple-300">Subject:</strong> {metadata.subject}</p>
          <p className="text-[10px] text-muted-foreground line-clamp-2">{metadata.description}</p>
        </div>
        {isLatest && onCreate && (
          <button onClick={onCreate} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 rounded-md text-[10px] font-medium text-purple-400">
            <Check className="w-3 h-3" />Create Ticket
          </button>
        )}
      </div>
    </div>
  )
}

function TicketCreatedCard({ metadata, onViewTickets }: { metadata: { subject: string }; onViewTickets?: () => void }) {
  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-green-500/15 via-emerald-500/10 to-green-500/15 border border-green-500/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-emerald-500/5" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-green-500/20"><CheckCircle2 className="w-3 h-3 text-green-400" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-green-400">Created</span>
        </div>
        <p className="text-xs text-foreground leading-relaxed mb-2">Ticket <strong className="text-green-300">"{metadata.subject}"</strong> created.</p>
        {onViewTickets && (
          <button onClick={onViewTickets} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/40 rounded-md text-[10px] font-medium text-green-400">
            <ExternalLink className="w-3 h-3" />View Tickets
          </button>
        )}
      </div>
    </div>
  )
}

function RentalSuggestionOfferCard({ content, onConfirm, isLatest }: { content: string; onConfirm?: () => void; isLatest: boolean }) {
  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-emerald-500/15 border border-emerald-500/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-teal-500/5 animate-pulse" style={{ animationDuration: '3s' }} />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-emerald-500/20"><Store className="w-3 h-3 text-emerald-400" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Suggest Rental</span>
        </div>
        <p className="text-xs text-foreground leading-relaxed mb-2">{content}</p>
        {isLatest && onConfirm && (
          <button onClick={onConfirm} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-md text-[10px] font-medium text-emerald-400">
            <Check className="w-3 h-3" />Submit
          </button>
        )}
      </div>
    </div>
  )
}

function RentalSuggestionDetailsCard({ metadata, onSubmit, isLatest }: { metadata: { rentalHouseName: string; rentalHouseUrl?: string }; onSubmit?: (details: string) => void; isLatest: boolean }) {
  const [country, setCountry] = useState('')
  const [url, setUrl] = useState(metadata.rentalHouseUrl || '')

  const handleSubmit = () => {
    if (country.trim() && onSubmit) {
      const details = url.trim() ? `Country: ${country.trim()}, Website: ${url.trim()}` : `Country: ${country.trim()}`
      onSubmit(details)
    }
  }

  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-emerald-500/15 border border-emerald-500/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-teal-500/5" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-emerald-500/20"><MapPin className="w-3 h-3 text-emerald-400" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Details</span>
        </div>
        <p className="text-xs text-foreground leading-relaxed mb-2">Help us track <strong className="text-emerald-300">{metadata.rentalHouseName}</strong>:</p>
        {isLatest && onSubmit && (
          <div className="space-y-2">
            <div>
              <label className="text-[10px] text-muted-foreground mb-0.5 block">Country *</label>
              <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g., Greece, USA" className="w-full px-2 py-1.5 bg-background/50 border border-border/50 rounded-md text-[10px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
            </div>
            {!metadata.rentalHouseUrl && (
              <div>
                <label className="text-[10px] text-muted-foreground mb-0.5 block">Website (optional)</label>
                <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." className="w-full px-2 py-1.5 bg-background/50 border border-border/50 rounded-md text-[10px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500/50" />
              </div>
            )}
            <button onClick={handleSubmit} disabled={!country.trim()} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 rounded-md text-[10px] font-medium text-emerald-400 disabled:opacity-50">
              <Check className="w-3 h-3" />Submit
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function RentalSuggestionSubmittedCard({ metadata }: { metadata: { name: string; url: string; country: string } }) {
  return (
    <div className="my-2 p-3 rounded-lg bg-gradient-to-r from-green-500/15 via-emerald-500/10 to-green-500/15 border border-green-500/40 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-emerald-500/5" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="p-1 rounded-md bg-green-500/20"><CheckCircle2 className="w-3 h-3 text-green-400" /></div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-green-400">Submitted</span>
        </div>
        <div className="space-y-0.5 text-xs text-foreground mb-1">
          <p><strong className="text-green-300">Name:</strong> {metadata.name}</p>
          {metadata.url && <p><strong className="text-green-300">Website:</strong> {metadata.url}</p>}
          <p><strong className="text-green-300">Country:</strong> {metadata.country}</p>
        </div>
        <p className="text-[10px] text-muted-foreground">Our team will review this soon.</p>
      </div>
    </div>
  )
}

// ============================================
// Message Content Parser
// ============================================
function MessageContent({ content, onRecommendationConfirm, onWhySubmit, onRentalSuggestionConfirm, onRentalSuggestionDetailsSubmit, onTicketCreate, onTicketGatheringSubmit, onViewTickets, isLatestMessage }: {
  content: string
  onRecommendationConfirm?: () => void
  onWhySubmit?: (reason: string) => void
  onRentalSuggestionConfirm?: () => void
  onRentalSuggestionDetailsSubmit?: (details: string) => void
  onTicketCreate?: (data: CreateTicketData) => void
  onTicketGatheringSubmit?: (details: string) => void
  onViewTickets?: () => void
  isLatestMessage: boolean
}) {
  if (!content) return <span className="text-muted-foreground">Thinking...</span>

  // All block types for pattern matching
  const BLOCK_TYPES = ['RECOMMENDATION_OFFER', 'WHY_QUESTION', 'RECOMMENDATION_SENT', 'TICKET_GATHERING', 'TICKET_OFFER', 'TICKET_CREATED', 'RENTAL_SUGGESTION_OFFER', 'RENTAL_SUGGESTION_DETAILS', 'RENTAL_SUGGESTION_SUBMITTED']
  const blockTypesPattern = BLOCK_TYPES.join('|')

  type PartType = 'text' | 'recommendation' | 'why_question' | 'recommendation_sent' | 'ticket_gathering' | 'ticket_offer' | 'ticket_created' | 'rental_suggestion_offer' | 'rental_suggestion_details' | 'rental_suggestion_submitted'
  
  // Helper to map block type to part type
  const mapBlockTypeToPartType = (blockType: string): PartType => {
    const mapping: Record<string, PartType> = {
      'RECOMMENDATION_OFFER': 'recommendation',
      'WHY_QUESTION': 'why_question',
      'RECOMMENDATION_SENT': 'recommendation_sent',
      'TICKET_GATHERING': 'ticket_gathering',
      'TICKET_OFFER': 'ticket_offer',
      'TICKET_CREATED': 'ticket_created',
      'RENTAL_SUGGESTION_OFFER': 'rental_suggestion_offer',
      'RENTAL_SUGGESTION_DETAILS': 'rental_suggestion_details',
      'RENTAL_SUGGESTION_SUBMITTED': 'rental_suggestion_submitted',
    }
    return mapping[blockType] || 'text'
  }

  const parts: Array<{ type: PartType; content: string }> = []
  // Match blocks with both opening and closing tags
  const blockRegex = new RegExp(`\\[(${blockTypesPattern})\\]([\\s\\S]*?)\\[\\/\\1\\]`, 'g')

  let lastIndex = 0
  let match
  while ((match = blockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) parts.push({ type: 'text', content: content.slice(lastIndex, match.index) })
    parts.push({ type: mapBlockTypeToPartType(match[1]), content: match[2].trim() })
    lastIndex = match.index + match[0].length
  }

  // Get remaining content after matched blocks
  let remainingContent = lastIndex < content.length ? content.slice(lastIndex) : ''
  
  // Strip orphaned closing tags from remaining content (AI model formatting issue)
  const orphanedCloseTagRegex = new RegExp(`\\[\\/(?:${blockTypesPattern})\\]`, 'g')
  remainingContent = remainingContent.replace(orphanedCloseTagRegex, '').trim()

  // Handle orphaned opening tags (AI forgot closing tag) - Pattern: [TAG]content... (no closing tag)
  const orphanedOpenRegex = new RegExp(`\\[(${blockTypesPattern})\\]([\\s\\S]*)$`)
  const orphanedMatch = remainingContent.match(orphanedOpenRegex)

  if (orphanedMatch) {
    // Content before the orphaned tag
    const beforeOrphan = remainingContent.slice(0, orphanedMatch.index)
    if (beforeOrphan.trim()) {
      parts.push({ type: 'text', content: beforeOrphan })
    }
    // The orphaned block content (render as card even without closing tag)
    parts.push({ type: mapBlockTypeToPartType(orphanedMatch[1]), content: orphanedMatch[2].trim() })
  } else if (remainingContent.trim()) {
    parts.push({ type: 'text', content: remainingContent })
  }

  if (parts.length === 0) parts.push({ type: 'text', content: content })

  return (
    <>
      {parts.map((part, partIndex) => {
        if (part.type === 'recommendation') return <RecommendationOfferCard key={`rec-${partIndex}`} content={part.content} onConfirm={onRecommendationConfirm} isLatest={isLatestMessage} />
        if (part.type === 'why_question') { try { return <WhyQuestionCard key={`why-${partIndex}`} metadata={JSON.parse(part.content)} onSubmit={onWhySubmit} isLatest={isLatestMessage} /> } catch { return null } }
        if (part.type === 'recommendation_sent') { try { return <RecommendationSentCard key={`sent-${partIndex}`} metadata={JSON.parse(part.content)} /> } catch { return null } }
        if (part.type === 'ticket_gathering') { try { return <TicketGatheringCard key={`ticket-gather-${partIndex}`} metadata={JSON.parse(part.content)} onSubmit={onTicketGatheringSubmit} isLatest={isLatestMessage} /> } catch { return null } }
        if (part.type === 'ticket_offer') { try { const metadata = JSON.parse(part.content); return <TicketOfferCard key={`ticket-offer-${partIndex}`} metadata={metadata} onCreate={() => onTicketCreate?.(metadata)} isLatest={isLatestMessage} /> } catch { return null } }
        if (part.type === 'ticket_created') { try { return <TicketCreatedCard key={`ticket-created-${partIndex}`} metadata={JSON.parse(part.content)} onViewTickets={onViewTickets} /> } catch { return null } }
        if (part.type === 'rental_suggestion_offer') return <RentalSuggestionOfferCard key={`rental-offer-${partIndex}`} content={part.content} onConfirm={onRentalSuggestionConfirm ?? onRecommendationConfirm} isLatest={isLatestMessage} />
        if (part.type === 'rental_suggestion_details') { try { return <RentalSuggestionDetailsCard key={`rental-details-${partIndex}`} metadata={JSON.parse(part.content)} onSubmit={onRentalSuggestionDetailsSubmit ?? onWhySubmit} isLatest={isLatestMessage} /> } catch { return null } }
        if (part.type === 'rental_suggestion_submitted') { try { return <RentalSuggestionSubmittedCard key={`rental-submitted-${partIndex}`} metadata={JSON.parse(part.content)} /> } catch { return null } }

        // Render normal text
        const lines = part.content.split('\n')
        const elements: React.ReactNode[] = []
        lines.forEach((line, i) => {
          const key = `${partIndex}-${i}`
          if (line.startsWith('### ')) elements.push(<h4 key={key} className="font-semibold mt-2 mb-1 text-foreground">{line.slice(4)}</h4>)
          else if (line.startsWith('## ')) elements.push(<h3 key={key} className="font-semibold mt-3 mb-1 text-foreground">{line.slice(3)}</h3>)
          else if (line.startsWith('# ')) elements.push(<h2 key={key} className="font-bold mt-3 mb-1 text-foreground">{line.slice(2)}</h2>)
          else if (line.startsWith('- ') || line.startsWith('* ')) elements.push(<div key={key} className="flex gap-2 ml-2"><span className="text-primary">•</span><span>{parseInlineMarkdown(line.slice(2))}</span></div>)
          else if (/^\d+\.\s/.test(line)) { const m = line.match(/^(\d+)\.\s(.*)$/); if (m) elements.push(<div key={key} className="flex gap-2 ml-2"><span className="text-muted-foreground">{m[1]}.</span><span>{parseInlineMarkdown(m[2])}</span></div>) }
          else if (line.trim() === '') elements.push(<div key={key} className="h-1.5" />)
          else elements.push(<p key={key}>{parseInlineMarkdown(line)}</p>)
        })
        return <div key={`text-${partIndex}`}>{elements}</div>
      })}
    </>
  )
}

function parseInlineMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => part.startsWith('**') && part.endsWith('**') ? <strong key={i} className="text-primary">{part.slice(2, -2)}</strong> : part)
}

function KeyHint({ children, className }: { children: string; className?: string }) {
  return (
    <kbd className={cx("text-foreground inline-flex h-4 items-center justify-center rounded border border-border bg-secondary px-1 font-mono text-[9px]", className)}>
      {children}
    </kbd>
  )
}

export default RigAssistantChat
